<template>
  <VTabs
    v-model="currentTab"
    density="compact"
    color="primary"
    class="text-secondary-text border-b-thin mb-4"
  >
    <VTab
      v-if="canViewIncomingConsultations"
      data-testid="consultation-panel-tab-received"
      value="received"
      class="text-tab p-0 mr-2"
    >
      {{ displayTabIncoming }}
    </VTab>
    <VTab
      v-if="canViewOutgoingConsultations"
      data-testid="consultation-panel-tab-sent"
      value="sent"
      class="text-tab p-0 mx-2"
    >
      {{ displayTabOutgoing }}
    </VTab>
    <VTab
      v-if="canSuperviseConsultations"
      data-testid="consultation-panel-tab-supervised"
      value="supervised"
      class="text-tab p-0 mx-2"
    >
      {{ displayTabSupervised }}
    </VTab>
    <VTab
      data-testid="consultation-panel-tab-completed"
      value="completed"
      class="text-tab p-0 ml-2"
    >
      {{ t('Completed') }}
    </VTab>
  </VTabs>
  <VTabsWindow v-model="currentTab">
    <VTabsWindowItem value="received">
      <ConsultationRequestList
        data-testid="consultation-list-received"
        :direct-consultations="receivedDirectConsultations"
        :scheduled-consultations="receivedScheduledConsultations"
        :current-tab="currentTab"
      />
    </VTabsWindowItem>
    <VTabsWindowItem value="sent">
      <ConsultationRequestList
        data-testid="consultation-list-sent"
        :direct-consultations="sentDirectConsultations"
        :scheduled-consultations="sentScheduledConsultations"
        :current-tab="currentTab"
      />
    </VTabsWindowItem>
    <VTabsWindowItem value="supervised">
      <ConsultationRequestList
        data-testid="consultation-list-supervised"
        :direct-consultations="supervisedDirectConsultations"
        :scheduled-consultations="supervisedScheduledConsultations"
        :current-tab="currentTab"
        :show-action-buttons="false"
        :show-edit-button="false"
      />
    </VTabsWindowItem>
    <VTabsWindowItem value="completed">
      <CompletedConsultationsTab />
    </VTabsWindowItem>
  </VTabsWindow>
</template>

<script setup lang="ts">
import ConsultationRequestList from '@/components/consultationrequest/ConsultationRequestList.vue'
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { storeToRefs } from 'pinia'
import { useUserStore } from '@/stores/user'
import type { ConsultationRequestDTO, ScheduledConsultationDTO } from '@/generated'
import type { ConsultationsViewTab } from '@/util/consultationsTab'
import CompletedConsultationsTab from '@/components/consultationrequest/CompletedConsultationsTab.vue'

const { t } = useI18n()

const {
  receivedDirectConsultations = [],
  sentDirectConsultations = [],
  receivedScheduledConsultations = [],
  sentScheduledConsultations = [],
  supervisedDirectConsultations = [],
  supervisedScheduledConsultations = [],
  initialTab = 'received'
} = defineProps<{
  receivedDirectConsultations?: ConsultationRequestDTO[]
  sentDirectConsultations?: ConsultationRequestDTO[]
  receivedScheduledConsultations?: ScheduledConsultationDTO[]
  sentScheduledConsultations?: ScheduledConsultationDTO[]
  supervisedDirectConsultations?: ConsultationRequestDTO[]
  supervisedScheduledConsultations?: ScheduledConsultationDTO[]
  initialTab?: ConsultationsViewTab
}>()

const { canViewIncomingConsultations, canViewOutgoingConsultations, canSuperviseConsultations } =
  storeToRefs(useUserStore())

const currentTab = ref<ConsultationsViewTab>(initialTab || 'received')

const emit = defineEmits<{
  'update:currentTab': [tab: ConsultationsViewTab]
}>()
watch(currentTab, (tab) => emit('update:currentTab', tab))

const displayTabIncoming = computed<string>(() => {
  return `${t('Received')} (${receivedDirectConsultations.length + receivedScheduledConsultations.length})`
})
const displayTabOutgoing = computed<string>(() => {
  return `${t('Sent')} (${sentDirectConsultations.length + sentScheduledConsultations.length})`
})
const displayTabSupervised = computed<string>(() => {
  return `${t('Supervised')} (${supervisedDirectConsultations.length + supervisedScheduledConsultations.length})`
})
</script>

<style scoped>
.text-tab {
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0;
  line-height: 24px;
}
</style>
